import { Alert, Card, SimpleGrid, Stack, Text } from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import {
  isBCTariffIngestResult,
  type BCTariffIngestResult,
  type BulkTariffIngestResult,
  type TariffIngestResult,
} from '@billing/validation/tariffIngestion';
import { TariffIngestIssues } from './TariffIngestIssues';

interface SummaryMetric {
  color: string;
  label: string;
  value: number;
}

const SummaryGrid = ({
  columns,
  metrics,
}: {
  columns: number;
  metrics: SummaryMetric[];
}) => (
  <SimpleGrid cols={{ base: 2, sm: columns }} spacing="sm">
    {metrics.map(({ color, label, value }) => (
      <Card withBorder radius="md" p="sm" key={label}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
          {label}
        </Text>
        <Text fw={700} size="xl" c={color}>
          {value}
        </Text>
      </Card>
    ))}
  </SimpleGrid>
);

const BCSummary = ({ result }: { result: BCTariffIngestResult }) => {
  const { translate } = useTranslation();
  const metrics: SummaryMetric[] = [
    {
      label: translate('tariffs.ingest.new', 'New'),
      value: result.newCount,
      color: 'green',
    },
    {
      label: translate('tariffs.ingest.changed', 'Changed'),
      value: result.changedCount,
      color: 'yellow',
    },
    {
      label: translate('tariffs.ingest.unchanged', 'Unchanged'),
      value: result.unchangedCount,
      color: 'blue',
    },
    {
      label: translate('tariffs.ingest.unsupported', 'Unsupported'),
      value: result.unsupportedCount,
      color: 'grape',
    },
    {
      label: translate('tariffs.ingest.failed', 'Failed'),
      value: result.failedCount,
      color: 'red',
    },
    {
      label: translate('tariffs.ingest.parsed', 'Parsed'),
      value: result.parsedCount,
      color: 'gray',
    },
  ];

  return (
    <Stack gap="sm">
      {result.parsedEffectiveDate && (
        <Alert color="blue">
          {translate(
            'tariffs.ingest.parsedEffectiveDate',
            'File effective date'
          )}
          : {result.parsedEffectiveDate}
        </Alert>
      )}
      <SummaryGrid columns={4} metrics={metrics} />
      <TariffIngestIssues
        failures={result.failures}
        warnings={result.warnings}
      />
    </Stack>
  );
};

const MBSummary = ({ result }: { result: BulkTariffIngestResult }) => {
  const { translate } = useTranslation();
  const metrics: SummaryMetric[] = [
    {
      label: translate('tariffs.ingest.created', 'Created'),
      value: result.createdCount,
      color: 'green',
    },
    {
      label: translate('tariffs.ingest.skipped', 'Skipped'),
      value: result.skippedCount,
      color: 'blue',
    },
    {
      label: translate('tariffs.ingest.failed', 'Failed'),
      value: result.failedCount,
      color: 'red',
    },
    {
      label: translate('tariffs.ingest.updated', 'Updated'),
      value: result.updatedCount,
      color: 'gray',
    },
    {
      label: translate('tariffs.ingest.rows', 'Rows'),
      value: result.totalRows,
      color: 'gray',
    },
  ];
  const details = [
    ...result.created.map(
      (tariff) =>
        `${translate('tariffs.ingest.row', 'Row')} ${tariff.row}: ${translate(
          'tariffs.ingest.createdLower',
          'created'
        )} ${tariff.tariff} - ${tariff.specialtyCode} ${
          tariff.effectiveDate
        } (${tariff.rate})`
    ),
    ...result.failed.map(
      (tariff) =>
        `${translate('tariffs.ingest.row', 'Row')} ${tariff.row}: ${translate(
          'tariffs.ingest.failedLower',
          'failed'
        )}${tariff.tariff ? ` ${tariff.tariff}` : ''} - ${tariff.reason}`
    ),
  ];

  return (
    <Stack gap="sm">
      <SummaryGrid columns={5} metrics={metrics} />
      {details.map((line) => (
        <Text size="sm" key={line}>
          {line}
        </Text>
      ))}
    </Stack>
  );
};

export const TariffIngestSummary = ({
  result,
}: {
  result: TariffIngestResult;
}) =>
  isBCTariffIngestResult(result) ? (
    <BCSummary result={result} />
  ) : (
    <MBSummary result={result} />
  );
